import React, { useState, useRef, useEffect } from 'react';
import { Upload, CheckCircle2, AlertCircle, Clock, User, ShieldAlert, Shield, Code, Eye, ChevronDown } from 'lucide-react';
import JsonView from '@uiw/react-json-view';
import { useProjectConfig } from '../hooks/useProjectConfig';
import PDTRecordFormattedView from './PDTRecordFormattedView';

interface MetricCardProps {
  label: string;
  value: string | number;
  alert?: boolean;
}

interface DataRowProps {
  label: string;
  value: any;
  mono?: boolean;
}

interface CollapsibleSectionProps {
  title: string;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const ClaudeArtifactViewer: React.FC = () => {
  const projectConfig = useProjectConfig();
  const [auditData, setAuditData] = useState<any>(null);
  const [rawJSON, setRawJSON] = useState<string>('');
  const [viewMode, setViewMode] = useState<'formatted' | 'raw'>('formatted');
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [jsonCollapseLevel, setJsonCollapseLevel] = useState<number | boolean>(2);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Get viewer configuration from project
  const viewerConfig = projectConfig.dataViewer;
  const recordTypeLabel = viewerConfig?.recordTypeLabel || 'Record';
  const dataType = viewerConfig?.dataType || 'custom';

  // Map icon names to components
  const iconMap = {
    'check': CheckCircle2,
    'alert': AlertCircle,
    'shield': ShieldAlert,
    'document': Code
  };

  // Use project-specific sample payloads
  const samplePayloads = (viewerConfig?.samplePayloads || []).map(sample => ({
    ...sample,
    icon: iconMap[sample.icon] || Code
  }));

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        parseJSON(e.target?.result as string);
      } catch (err: any) {
        setError(`Error reading file: ${err.message}`);
      }
    };
    reader.readAsText(file);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const pastedText = event.clipboardData.getData('text');
    try {
      parseJSON(pastedText);
    } catch (err: any) {
      setError(`Error parsing JSON: ${err.message}`);
    }
  };

  const parseJSON = (jsonText: string) => {
    try {
      setError(null);
      setRawJSON(jsonText);
      const outerJson = JSON.parse(jsonText);

      // Parse the nested Message string if it exists
      if (outerJson.Message && typeof outerJson.Message === 'string') {
        outerJson.ParsedMessage = JSON.parse(outerJson.Message);
      }

      setAuditData(outerJson);
      setViewMode('formatted');
      setJsonCollapseLevel(2); // Reset to default level
    } catch (err: any) {
      setError(`Failed to parse JSON: ${err.message}`);
      setAuditData(null);
    }
  };

  const loadSamplePayload = async (path: string) => {
    try {
      setError(null);
      const response = await fetch(path);
      if (!response.ok) {
        throw new Error(`Failed to load sample: ${response.statusText}`);
      }
      const jsonText = await response.text();
      parseJSON(jsonText);
    } catch (err: any) {
      setError(`Failed to load sample payload: ${err.message}`);
    }
  };

  const resetViewer = () => {
    setAuditData(null);
    setRawJSON('');
    setViewMode('formatted');
    setError(null);
    setExpandedSections({});
  };

  // Recursively parse nested JSON strings for better viewing
  const parseNestedJSON = (obj: any): any => {
    if (obj === null || obj === undefined) return obj;

    if (typeof obj === 'string') {
      try {
        const parsed = JSON.parse(obj);
        if (typeof parsed === 'object') {
          return parseNestedJSON(parsed);
        }
      } catch {
        return obj;
      }
      return obj;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => parseNestedJSON(item));
    }

    if (typeof obj === 'object') {
      const result: any = {};
      for (const key in obj) {
        result[key] = parseNestedJSON(obj[key]);
      }
      return result;
    }

    return obj;
  };

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
  };

  const renderContentPolicyFilters = (assessment: any) => {
    if (!assessment?.trace_data?.guardrail?.input) return null;

    const guardrailData = Object.values(assessment.trace_data.guardrail.input)[0] as any;
    const contentPolicy = guardrailData?.contentPolicy;

    if (!contentPolicy?.filters || contentPolicy.filters.length === 0) return null;

    return (
      <div className="mt-3">
        <div className="font-semibold mb-2">Detected Policy Violations:</div>
        <div className="space-y-2">
          {contentPolicy.filters.map((filter: any, idx: number) => (
            <div key={idx} className="bg-white border border-red-200 rounded p-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-red-900">{filter.type}</span>
                <span className={`px-2 py-0.5 rounded ${
                  filter.confidence === 'HIGH' ? 'bg-red-200 text-red-900' :
                  filter.confidence === 'MEDIUM' ? 'bg-orange-200 text-orange-900' :
                  'bg-yellow-200 text-yellow-900'
                }`}>
                  {filter.confidence} Confidence
                </span>
              </div>
              <div className="mt-1 text-gray-600">
                <span>Action: </span>
                <span className="font-semibold text-red-800">{filter.action}</span>
                <span className="mx-2">•</span>
                <span>Strength: {filter.filterStrength}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderGuardrailMetrics = (assessment: any) => {
    const inputMetrics = assessment.trace_data?.guardrail?.input?.[assessment.guardrail_id];
    const outputMetrics = assessment.trace_data?.guardrail?.outputs?.[0]?.[assessment.guardrail_id];

    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
          {inputMetrics && (
            <div className="bg-gray-50 p-3 rounded">
              <h4 className="font-semibold mb-2">Input Processing</h4>
              <DataRow label="Processing Latency" value={`${inputMetrics.invocationMetrics?.guardrailProcessingLatency}ms`} />
              <DataRow label="Text Characters Guarded" value={inputMetrics.invocationMetrics?.guardrailCoverage?.textCharacters?.guarded?.toLocaleString()} />
              <DataRow label="Topic Policy Units" value={inputMetrics.invocationMetrics?.usage?.topicPolicyUnits} />
              <DataRow label="Content Policy Units" value={inputMetrics.invocationMetrics?.usage?.contentPolicyUnits} />
              <DataRow label="Sensitive Info Units" value={inputMetrics.invocationMetrics?.usage?.sensitiveInformationPolicyUnits} />
            </div>
          )}
          {outputMetrics && (
            <div className="bg-gray-50 p-3 rounded">
              <h4 className="font-semibold mb-2">Output Processing</h4>
              <DataRow label="Processing Latency" value={`${outputMetrics.invocationMetrics?.guardrailProcessingLatency}ms`} />
              <DataRow label="Text Characters Guarded" value={outputMetrics.invocationMetrics?.guardrailCoverage?.textCharacters?.guarded?.toLocaleString()} />
              <DataRow label="Topic Policy Units" value={outputMetrics.invocationMetrics?.usage?.topicPolicyUnits} />
              <DataRow label="Content Policy Units" value={outputMetrics.invocationMetrics?.usage?.contentPolicyUnits} />
            </div>
          )}
        </div>

        <div className="bg-blue-50 p-3 rounded">
          <h4 className="font-semibold mb-2">Guardrail Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            <DataRow label="Guardrail ID" value={assessment.guardrail_id} mono />
            <DataRow label="Version" value={assessment.guardrail_version} />
            <DataRow label="Action" value={assessment.action} />
            {inputMetrics?.appliedGuardrailDetails && (
              <>
                <DataRow label="ARN" value={inputMetrics.appliedGuardrailDetails.guardrailArn} mono />
                <DataRow label="Ownership" value={inputMetrics.appliedGuardrailDetails.guardrailOwnership} />
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderSummary = () => {
    if (!auditData?.ParsedMessage) return null;
    const msg = auditData.ParsedMessage;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="border-b pb-4">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {recordTypeLabel}: {msg.topic?.replace(/-/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase()) || 'Assessment'}
          </h1>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              {msg.response_summary?.success ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600" />
              )}
              <span className="font-semibold">
                {msg.response_summary?.success ? 'Processed Successfully' : 'Processing Failed'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <span>{formatTimestamp(msg.timestamp)}</span>
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-2">
            Request ID: <code className="bg-gray-100 px-1 rounded">{msg.request_id}</code>
          </div>
        </div>

        {/* Guardrail Intervention Alert */}
        {msg.guardrail_triggered && msg.guardrail_intervention && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <div className="flex items-start gap-3">
              <ShieldAlert className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h2 className="font-bold text-red-900 mb-2">⚠️ Guardrail Intervention Triggered</h2>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-semibold">Block Type:</span>{' '}
                    <span className="bg-red-100 px-2 py-0.5 rounded text-red-800">
                      {msg.guardrail_intervention.block_type?.toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <span className="font-semibold">Block Reason:</span>{' '}
                    <span className="bg-red-100 px-2 py-0.5 rounded text-red-800">
                      {msg.guardrail_intervention.block_reason?.replace(/_/g, ' ').toUpperCase()}
                    </span>
                  </div>
                  {renderContentPolicyFilters(msg.guardrail_assessment)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* User Question */}
        {msg.user_input && (
          <div className={`${msg.guardrail_triggered ? 'bg-orange-50 border-l-4 border-orange-500' : 'bg-blue-50 border-l-4 border-blue-500'} p-4 rounded`}>
            <h2 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
              <User className="w-4 h-4" />
              User Question {msg.guardrail_triggered && <span className="text-red-600 text-xs">(Blocked)</span>}
            </h2>
            <p className="text-gray-700 italic">"{msg.user_input.trim()}"</p>
          </div>
        )}

        {/* AI Assessment */}
        {msg.llm_response && (
          <div className={`border rounded-lg p-6 shadow-sm ${msg.response_summary?.blocked ? 'bg-red-50 border-red-300' : 'bg-white border-gray-200'}`}>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              AI Assessment
              {msg.response_summary?.blocked && (
                <span className="ml-2 text-sm font-normal text-red-600">(Request Blocked)</span>
              )}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-xs text-gray-500 mb-1">Decision</div>
                <div className="font-semibold text-gray-900">{msg.llm_response.determination || 'N/A'}</div>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-xs text-gray-500 mb-1">Confidence</div>
                <div className="font-semibold text-gray-900">
                  {msg.llm_response.confidence ? `${(msg.llm_response.confidence * 100).toFixed(0)}%` : 'N/A'}
                </div>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <div className="text-xs text-gray-500 mb-1">Safety Check</div>
                <div className="font-semibold">
                  {msg.guardrail_triggered ? (
                    <span className="text-red-600 flex items-center gap-1">
                      <ShieldAlert className="w-4 h-4" />
                      Triggered
                    </span>
                  ) : (
                    <span className="text-green-600 flex items-center gap-1">
                      <Shield className="w-4 h-4" />
                      Passed
                    </span>
                  )}
                </div>
              </div>
            </div>

            {msg.llm_response.reasoning && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Reasoning</h3>
                <p className="text-gray-700">{msg.llm_response.reasoning}</p>
              </div>
            )}

            {msg.llm_response.recommendation && (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                <h3 className="font-semibold text-gray-900 mb-2">Recommendation</h3>
                <p className="text-gray-700">{msg.llm_response.recommendation}</p>
              </div>
            )}
          </div>
        )}

        {/* Key Metrics */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Key Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard label="User Input Length" value={`${msg.user_input_length || 0} chars`} />
            <MetricCard label="Confidence" value={msg.llm_response?.confidence ? `${(msg.llm_response.confidence * 100).toFixed(0)}%` : 'N/A'} />
            <MetricCard
              label="Safety Issues"
              value={msg.guardrail_triggered ? '1+' : '0'}
              alert={msg.guardrail_triggered}
            />
            <MetricCard label="Citations" value={msg.llm_response?.citations_count || 0} />
          </div>
        </div>

        {/* Collapsible Details */}
        <div className="space-y-3">
          {/* Session Information */}
          <CollapsibleSection
            title="Session Information"
            isExpanded={expandedSections.session}
            onToggle={() => toggleSection('session')}
          >
            <div className="grid grid-cols-2 gap-3 text-sm">
              <DataRow label="User ID" value={msg.user_id} />
              <DataRow label="Session ID" value={msg.session_id} />
              <DataRow label="Organization" value={msg.organization_id} />
              <DataRow label="Interaction Type" value={msg.interaction_type} />
              <DataRow label="Topic" value={msg.topic} />
            </div>
          </CollapsibleSection>

          {/* Response Processing */}
          <CollapsibleSection
            title="Response Processing"
            isExpanded={expandedSections.processing}
            onToggle={() => toggleSection('processing')}
          >
            <div className="grid grid-cols-2 gap-3 text-sm">
              <DataRow label="Handler Mode" value={msg.response_summary?.handler_mode} />
              <DataRow label="Source" value={msg.response_summary?.source} />
              <DataRow label="Blocked" value={msg.response_summary?.blocked ? 'Yes' : 'No'} />
              <DataRow label="Success" value={msg.response_summary?.success ? 'Yes' : 'No'} />
            </div>
          </CollapsibleSection>

          {/* Guardrail Metrics */}
          {msg.guardrail_assessment && (
            <CollapsibleSection
              title={msg.guardrail_triggered ? "⚠️ Guardrail Metrics (Intervention Triggered)" : "Guardrail Metrics"}
              isExpanded={expandedSections.guardrails}
              onToggle={() => toggleSection('guardrails')}
            >
              <div className="space-y-4">
                {msg.guardrail_triggered && msg.guardrail_intervention && (
                  <div className="bg-red-50 border border-red-200 rounded p-4 mb-4">
                    <h4 className="font-semibold text-red-900 mb-3">Intervention Details</h4>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <DataRow label="Block Type" value={msg.guardrail_intervention.block_type} />
                      <DataRow label="Block Reason" value={msg.guardrail_intervention.block_reason?.replace(/_/g, ' ')} />
                      <DataRow label="Guardrail ID" value={msg.guardrail_intervention.guardrail_id} mono />
                      <DataRow label="Guardrail Version" value={msg.guardrail_intervention.guardrail_version} />
                      <DataRow label="Action" value={msg.guardrail_assessment.action} />
                      <DataRow label="Action Reason" value={msg.guardrail_assessment.trace_data?.guardrail?.actionReason} />
                    </div>
                    {renderContentPolicyFilters(msg.guardrail_assessment)}
                  </div>
                )}
                {renderGuardrailMetrics(msg.guardrail_assessment)}
              </div>
            </CollapsibleSection>
          )}

          {/* SNS Notification */}
          <CollapsibleSection
            title="SNS Notification Details"
            isExpanded={expandedSections.sns}
            onToggle={() => toggleSection('sns')}
          >
            <div className="space-y-2 text-sm">
              <DataRow label="Topic ARN" value={auditData.TopicArn} mono />
              <DataRow label="Message ID" value={auditData.MessageId} mono />
              <DataRow label="Subject" value={auditData.Subject} />
              <DataRow label="Timestamp" value={formatTimestamp(auditData.Timestamp)} />
            </div>
          </CollapsibleSection>
        </div>
      </div>
    );
  };

  return (
    <div className="flex-1 overflow-auto bg-gray-50">
      <div className="max-w-6xl mx-auto p-6">
        {!auditData ? (
          <div className="bg-white rounded-lg shadow-lg p-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{viewerConfig?.menuLabel || 'Data Viewer'}</h1>
            <p className="text-gray-600 mb-6">{viewerConfig?.description || 'View and analyze JSON data records'}</p>

            {/* Sample Payloads - Rich Custom Dropdown */}
            {samplePayloads.length > 0 && (
              <div className="mb-8">
                <label className="block text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Code className="w-5 h-5 text-indigo-600" />
                  Try Sample Payloads
                </label>
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="w-full px-4 py-4 border-2 border-indigo-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-gradient-to-r from-indigo-50 to-purple-50 text-gray-900 cursor-pointer hover:border-indigo-500 transition-all shadow-sm hover:shadow-md flex items-center justify-between"
                  >
                    <span className="font-medium text-indigo-900">Select a sample to load...</span>
                    <ChevronDown className={`w-5 h-5 text-indigo-600 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute z-10 w-full mt-2 bg-white border-2 border-indigo-200 rounded-lg shadow-xl max-h-96 overflow-auto">
                      {samplePayloads.map((sample) => {
                        const IconComponent = sample.icon;
                        const colorClasses = {
                          green: 'hover:bg-green-50 border-l-4 border-l-green-500',
                          red: 'hover:bg-red-50 border-l-4 border-l-red-500',
                          blue: 'hover:bg-blue-50 border-l-4 border-l-blue-500',
                          yellow: 'hover:bg-yellow-50 border-l-4 border-l-yellow-500'
                        };
                        const iconColors = {
                          green: 'text-green-600',
                          red: 'text-red-600',
                          blue: 'text-blue-600',
                          yellow: 'text-yellow-600'
                        };
                        const badgeColors = {
                          green: 'bg-green-100 text-green-800',
                          red: 'bg-red-100 text-red-800',
                          blue: 'bg-blue-100 text-blue-800',
                          yellow: 'bg-yellow-100 text-yellow-800'
                        };

                        return (
                          <button
                            key={sample.id}
                            onClick={() => {
                              loadSamplePayload(sample.path);
                              setIsDropdownOpen(false);
                            }}
                            className={`w-full text-left px-4 py-4 transition-all ${colorClasses[sample.color]} border-b border-gray-100 last:border-b-0`}
                          >
                            <div className="flex items-start gap-3">
                              <IconComponent className={`w-6 h-6 ${iconColors[sample.color]} flex-shrink-0 mt-1`} />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <h3 className="font-semibold text-gray-900">
                                    {sample.title}
                                  </h3>
                                  <span className={`text-xs px-2 py-0.5 rounded-full ${badgeColors[sample.color]}`}>
                                    {sample.color.toUpperCase()}
                                  </span>
                                </div>
                                <p className="text-sm text-gray-600">{sample.description}</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="border-t-2 border-indigo-100 pt-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Upload className="w-5 h-5 text-emerald-600" />
                Or Load Your Own Data
              </h2>

              {/* File Upload */}
              <div className="mb-6">
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-emerald-300 border-dashed rounded-lg cursor-pointer bg-gradient-to-br from-emerald-50 to-teal-50 hover:from-emerald-100 hover:to-teal-100 transition-all shadow-sm hover:shadow-md">
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <Upload className="w-10 h-10 text-emerald-500 mb-2" />
                    <p className="mb-1 text-sm text-gray-700">
                      <span className="font-semibold text-emerald-700">Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-emerald-600">JSON data file</p>
                  </div>
                  <input
                    type="file"
                    className="hidden"
                    accept=".json"
                    onChange={handleFileUpload}
                  />
                </label>
              </div>

              {/* Paste Area */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-2 flex items-center gap-2">
                  <Code className="w-4 h-4 text-purple-600" />
                  Or paste JSON directly:
                </label>
                <textarea
                  className="w-full h-24 px-4 py-3 border-2 border-purple-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 font-mono text-sm bg-gradient-to-br from-purple-50 to-pink-50 hover:from-purple-100 hover:to-pink-100 transition-all"
                  placeholder="Paste JSON here..."
                  onPaste={handlePaste}
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                <strong>Error:</strong> {error}
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-lg p-8">
            {/* Navigation Bar */}
            <div className="flex items-center justify-between mb-6 pb-4 border-b">
              <button
                onClick={resetViewer}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition flex items-center gap-2"
              >
                <Upload className="w-4 h-4" />
                Load Different File
              </button>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setViewMode('formatted')}
                  className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
                    viewMode === 'formatted'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Eye className="w-4 h-4" />
                  Formatted View
                </button>
                <button
                  onClick={() => setViewMode('raw')}
                  className={`px-4 py-2 rounded-lg transition flex items-center gap-2 ${
                    viewMode === 'raw'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Code className="w-4 h-4" />
                  JSON Explorer
                </button>
              </div>
            </div>

            {/* Content */}
            {viewMode === 'formatted' ? (
              dataType === 'pdt-record' ? (
                <PDTRecordFormattedView data={auditData} />
              ) : (
                renderSummary()
              )
            ) : (
              <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 bg-gray-50 border-b">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h2 className="text-lg font-semibold text-gray-900 mb-1">JSON Explorer</h2>
                      <p className="text-sm text-gray-600">
                        Click to expand/collapse sections. Nested JSON strings are automatically parsed for readability.
                      </p>
                    </div>
                  </div>

                  {/* Expand/Collapse Controls */}
                  <div className="flex items-center gap-2 pt-3 border-t">
                    <span className="text-xs font-semibold text-gray-600 mr-2">Expand/Collapse:</span>
                    <button
                      onClick={() => setJsonCollapseLevel(false)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        jsonCollapseLevel === false
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      All
                    </button>
                    <button
                      onClick={() => setJsonCollapseLevel(1)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        jsonCollapseLevel === 1
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Level 1
                    </button>
                    <button
                      onClick={() => setJsonCollapseLevel(2)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        jsonCollapseLevel === 2
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Level 2
                    </button>
                    <button
                      onClick={() => setJsonCollapseLevel(3)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        jsonCollapseLevel === 3
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Level 3
                    </button>
                    <button
                      onClick={() => setJsonCollapseLevel(true)}
                      className={`px-3 py-1 rounded-md text-xs font-medium transition-colors ${
                        jsonCollapseLevel === true
                          ? 'bg-indigo-600 text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Collapse All
                    </button>
                  </div>
                </div>
                <div className="p-6 overflow-x-auto">
                  <JsonView
                    value={parseNestedJSON(JSON.parse(rawJSON))}
                    collapsed={jsonCollapseLevel}
                    displayDataTypes={false}
                    shortenTextAfterLength={0}
                    style={{
                      fontSize: '13px',
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                      lineHeight: '1.6'
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

function MetricCard({ label, value, alert = false }: MetricCardProps) {
  return (
    <div className={`${alert ? 'bg-red-50 border border-red-200' : 'bg-gray-50'} p-3 rounded`}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-lg font-semibold ${alert ? 'text-red-600' : 'text-gray-900'}`}>
        {value}
      </div>
    </div>
  );
}

function DataRow({ label, value, mono = false }: DataRowProps) {
  return (
    <div className="py-1">
      <span className="text-gray-500">{label}:</span>{' '}
      <span className={`text-gray-900 ${mono ? 'font-mono text-xs' : ''}`}>
        {value || 'N/A'}
      </span>
    </div>
  );
}

function CollapsibleSection({ title, isExpanded, onToggle, children }: CollapsibleSectionProps) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition flex items-center justify-between"
      >
        <span className="font-semibold text-gray-900">{title}</span>
        <span className="text-gray-500">
          {isExpanded ? '▼' : '▶'}
        </span>
      </button>
      {isExpanded && (
        <div className="px-4 py-3 bg-white">
          {children}
        </div>
      )}
    </div>
  );
}

export default ClaudeArtifactViewer;
